/**
 * Prompt Service - Handles API interactions for prompt templates and libraries
 */

import axios from 'axios';
import { get, post, put, patch, getWithoutDeduplication } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/mms_persistence/prompt`;
const PROMPTS_URL = `/api/v1/mms_ai_configs/prompt/config`

// /**
//  * Get all prompts with optional filtering
//  * @param {Object} filters - Filter parameters
//  * @returns {Promise<Array>} Promise resolving to array of prompts
//  */
// export const getPrompts = async (filter = {}, sort = {}, query = {}) => {
//   try {
//     const {skip = 0, limit = 10} = query;
//     const response = await post(`${API_URL}/fetch?query=skip=0&limit=100`, { filter, sort, skip, limit });
//     return response;
//   } catch (error) {
//     console.error('Error fetching prompts:', error);
//     throw error;
//   }
// };

/**
 * Get all prompts with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of prompts
 */
export const getPrompts = async (filter = {}, sort = {}, query = {}) => {
  try {
    const {skip = 0, limit = 10} = query;
    const response = await post(`${PROMPTS_URL}/fetch?`, { filter, sort, skip, limit });
    return response;
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

/**
 * Get a specific prompt by ID
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Object>} Promise resolving to prompt object
 */
export const getPromptById = async (promptId) => {
  try {
    const response = await get(`${PROMPTS_URL}/get_by_id/${promptId}`);
    return response;
  } catch (error) {
    console.error(`Error fetching prompt ${promptId}:`, error);
    throw error;
  }
};


/**
 * Get a specific prompt by code
 * @param {string} promptcode - Prompt Code
 * @returns {Promise<Object>} Promise resolving to prompt object
 */
export const getPromptByCode = async (promptCode) => {
  try {
    const response = await getWithoutDeduplication(`${PROMPTS_URL}get_by_cd/${promptCode}`);
    // const response = await get(`${PROMPTS_URL}get_by_cd/${promptCode}`);
    return response;
  } catch (error) {
    console.error(`Error fetching prompt ${promptCode}:`, error);
    throw error;
  }
};

/**
 * Create a new prompt
 * @param {Object} promptData - Prompt data
 * @returns {Promise<Object>} Promise resolving to created prompt
 */
export const createPrompt = async (promptData) => {
  try {
    // Explicitly exclude 'id' field to let backend auto-generate it
    const { id, ...dataWithoutId } = promptData;
    const response = await post(`${PROMPTS_URL}/`, dataWithoutId);
    // const response = await post(`${PROMPTS_URL}`, dataWithoutId);
    return response;
  } catch (error) {
    console.error('Error creating prompt:', error);
    throw error;
  }
};

/**
 * Update an existing prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} promptData - Updated prompt data
 * @returns {Promise<Object>} Promise resolving to updated prompt
 */
export const updatePrompt = async (promptData) => {
  try {
    const response = await put(`${PROMPTS_URL}/`, promptData);
    // const response = await put(`${PROMPTS_URL}`, promptData);
    return response;
  } catch (error) {
    console.error(`Error updating prompt:`, error);
    throw error;
  }
};

/**
 * Delete a prompt
 * @param {string} promptId - Prompt ID
 * @returns {Promise<void>} Promise resolving when prompt is deleted
 */
export const deletePrompt = async (promptId) => {
  try {
    await axios.delete(`${API_URL}/${promptId}`);
  } catch (error) {
    console.error(`Error deleting prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Activate a prompt
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Object>} Promise resolving to activated prompt
 */
export const activatePrompt = async (promptId) => {
  try {
    const response = await patch(`${PROMPTS_URL}/activate/${promptId}`, {});
    return response;
  } catch (error) {
    console.error(`Error activating prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Deactivate a prompt
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Object>} Promise resolving to deactivated prompt
 */
export const deactivatePrompt = async (promptId) => {
  try {
    const response = await patch(`${PROMPTS_URL}/close/${promptId}`, {});
    return response;
  } catch (error) {
    console.error(`Error deactivating prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt variables
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of prompt variables
 */
export const getPromptVariables = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/variables`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching variables for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Update prompt variables
 * @param {string} promptId - Prompt ID
 * @param {Array} variables - Updated variables
 * @returns {Promise<Object>} Promise resolving to updated prompt
 */
export const updatePromptVariables = async (promptId, variables) => {
  try {
    const response = await axios.put(`${API_URL}/${promptId}/variables`, { variables });
    return response.data;
  } catch (error) {
    console.error(`Error updating variables for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt examples
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of prompt examples
 */
export const getPromptExamples = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/examples`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching examples for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add a prompt example
 * @param {string} promptId - Prompt ID
 * @param {Object} example - Example data
 * @returns {Promise<Object>} Promise resolving to created example
 */
export const addPromptExample = async (promptId, example) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/examples`, example);
    return response.data;
  } catch (error) {
    console.error(`Error adding example to prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Delete a prompt example
 * @param {string} promptId - Prompt ID
 * @param {string} exampleId - Example ID
 * @returns {Promise<void>} Promise resolving when example is deleted
 */
export const deletePromptExample = async (promptId, exampleId) => {
  try {
    await axios.delete(`${API_URL}/${promptId}/examples/${exampleId}`);
  } catch (error) {
    console.error(`Error deleting example ${exampleId} from prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Execute a prompt with provided variables
 * @param {string} promptId - Prompt ID
 * @param {Object} variables - Variables to use in the prompt
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const executePrompt = async (promptId, variables) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/execute`, { variables });
    return response.data;
  } catch (error) {
    console.error(`Error executing prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt usage statistics
 * @param {string} promptId - Prompt ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const getPromptUsage = async (promptId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/usage`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching usage for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt categories
 * @returns {Promise<Array>} Promise resolving to array of prompt categories
 */
export const getPromptCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt categories:', error);
    throw error;
  }
};

/**
 * Create a new prompt category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Promise resolving to created category
 */
export const createPromptCategory = async (categoryData) => {
  try {
    const response = await axios.post(`${API_URL}/categories`, categoryData);
    return response.data;
  } catch (error) {
    console.error('Error creating prompt category:', error);
    throw error;
  }
};

/**
 * Get prompt feedback
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of feedback
 */
export const getPromptFeedback = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/feedback`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching feedback for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add feedback for a prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} feedback - Feedback data
 * @returns {Promise<Object>} Promise resolving to created feedback
 */
export const addPromptFeedback = async (promptId, feedback) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/feedback`, feedback);
    return response.data;
  } catch (error) {
    console.error(`Error adding feedback to prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add feedback for a prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} feedback - Feedback data
 * @returns {Promise<Object>} Promise resolving to created feedback
 */

export const getPromptCount = async (filter = {}) => {
   try {
     const response = await post(`${PROMPTS_URL}/count`, filter);
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};

export default {
  getPrompts,
  getPromptById,
  createPrompt,
  updatePrompt,
  deletePrompt,
  getPromptVariables,
  updatePromptVariables,
  getPromptExamples,
  addPromptExample,
  deletePromptExample,
  executePrompt,
  getPromptUsage,
  getPromptCategories,
  createPromptCategory,
  getPromptFeedback,
  addPromptFeedback
};

